import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { AircallAnbindungLeitungen, ApiClient } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Chip, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../components/meHuellen.js";
import { LeitungModal, anrufeText, leitungText } from "../components/AircallLeitungModal.js";
import type { AircallLeitungClient } from "../components/AircallLeitungModal.js";
import { formatDatum } from "../format.js";
import "./KundeAircallLeitungen.css";

/**
 * Die LEITUNGEN der Aircall-Anbindung in der Kundenakte (Spec „Nacharbeitszeit, Leitung und Kunde je Anruf",
 * Fassung 1.3, V2, V5, E304, E311, E313, F12–F17): je heute zugeordneter Leitung Name, Nummer und „gültig ab", darunter
 * eingeklappt die Zeitreihe (frühere und künftige Zeilen, widerrufene durchgestrichen). Zuordnen, Beenden und
 * Widerrufen nur für ADMIN — immer mit gestapelter Bestätigung. Jede Änderung dreht die Version der Anbindung; der
 * Abschnitt meldet sie nach oben (`onVersion`), damit „Anbindung ändern" nicht in eine 409 läuft. Block `kal`.
 */

export type KundeAircallLeitungenClient = AircallLeitungClient
  & Pick<ApiClient, "aircallAnbindungLeitungen" | "widerrufeAircallZuordnung">;

export type KundeAircallLeitungenProps = {
  client: KundeAircallLeitungenClient;
  customerId: string;
  kundeName: string | null;
  istAdmin: boolean;
  verdeckt?: boolean | undefined;
  /** Die Version der Anbindung, wie der Kopf sie kennt; ist sie neuer als die Sicht (Kopfdaten geändert), lädt der
   *  Abschnitt die Sicht neu — sonst liefe die nächste Zuordnung in eine 409. */
  anbindungVersion?: number | undefined;
  onVersion: (version: number) => void;
};

const ZIEL: Record<AircallAnbindungLeitungen["zuordnungen"][number]["ziel"], string> = {
  DIESE: "dieser Kunde", ANDERE: "anderer Kunde", KEINE: "keinem Kunden",
};

type Zeile = AircallAnbindungLeitungen["zuordnungen"][number];

/**
 * Darf die Kundenakte diese Zeile widerrufen (Ruling R-A7, Fix-Runde 2)? Nur eine Zeile DIESER Anbindung — oder eine
 * Zeile „keinem Kunden", deren gültiger Vorgänger dieser Anbindung gehört (das eigene Ende). Das Ende, das eine andere
 * Anbindung gesetzt hat, widerrufen hieße, dort wieder abzurechnen. Dieselbe Regel prüft der Server (422).
 */
function eigeneZeile(z: Zeile, alle: readonly Zeile[]): boolean {
  if (z.ziel !== "KEINE") return z.ziel === "DIESE";
  const vorgaenger = alle
    .filter((x) => x.leitungId === z.leitungId && x.widerrufenAm === null && x.validFrom < z.validFrom)
    .reduce<Zeile | null>((bisher, x) => (bisher === null || x.validFrom > bisher.validFrom ? x : bisher), null);
  return vorgaenger?.ziel === "DIESE";
}

function zielText(z: Zeile): string {
  return z.ziel === "ANDERE" ? z.kundeName ?? ZIEL.ANDERE : ZIEL[z.ziel];
}

export function KundeAircallLeitungen(p: KundeAircallLeitungenProps): ReactElement {
  const { client, customerId } = p;
  const [sicht, setSicht] = useState<AircallAnbindungLeitungen | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [zeitreihe, setZeitreihe] = useState(false);
  const [modal, setModal] = useState<{ modus: "ZUORDNEN" | "BEENDEN"; leitungId?: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const { bestaetigen } = useBestaetigung();
  const lauf = useRef(0);
  const laden = client.aircallAnbindungLeitungen;

  useEffect(() => {
    const meiner = ++lauf.current;
    setSicht(null);
    setMeldung(null);
    laden(customerId)
      .then((s) => { if (meiner === lauf.current) setSicht(s); })
      .catch((e: unknown) => {
        if (meiner === lauf.current) {
          setMeldung(e instanceof ApiError ? e.message : "Die Leitungen konnten nicht geladen werden.");
        }
      });
    return () => { lauf.current += 1; };
  }, [laden, customerId]);

  /**
   * Nach einer 409 bzw. geänderten Kopfdaten: die Sicht neu holen, OHNE sie zu leeren (ein offenes Modal bleibt).
   * Gelingt es, steht danach `hinweis` (Vorgabe: keine Meldung — ein alter roter Hinweis verschwindet).
   */
  function neuLaden(hinweis: string | null = null): void {
    const meiner = lauf.current;
    laden(customerId)
      .then((s) => {
        if (meiner === lauf.current) { setSicht(s); setMeldung(hinweis); p.onVersion(s.version); }
      })
      .catch((e: unknown) => {
        if (meiner === lauf.current) {
          setMeldung(e instanceof ApiError ? e.message : "Die Leitungen konnten nicht geladen werden.");
        }
      });
  }

  const veraltet = sicht !== null && p.anbindungVersion !== undefined && p.anbindungVersion > sicht.version;
  // Absichtlich nur `veraltet` als Abhängigkeit: geladen wird beim Umschlagen auf „veraltet", nicht bei jedem Zeichnen.
  useEffect(() => { if (veraltet) neuLaden(); }, [veraltet]);

  function uebernehmen(neu: AircallAnbindungLeitungen): void {
    setSicht(neu);
    setMeldung(null);
    setModal(null);
    p.onVersion(neu.version);
  }

  /**
   * Widerrufen — nur Zeilen DIESER Anbindung (Ruling R-A7; der Server lehnt fremde mit 422 ab). Die Gefahr-Frage nennt
   * wie Zuordnen und Beenden die Zahl der Anrufe, deren Abrechnungskunde wechselt (E313): ab dem Tag der Zeile gilt
   * wieder ihr Vorgänger.
   */
  async function widerrufen(z: Zeile): Promise<void> {
    if (sicht === null || laeuft) return;
    setLaeuft(true);
    try {
      const { anrufe } = await client.aircallAuswirkung(customerId, z.leitungId,
        { validFrom: z.validFrom, widerruf: z.id });
      if (!(await bestaetigen({ titel: "Zuordnung widerrufen?", bestaetigenText: "Widerrufen", gefaehrlich: true,
        text: `Die Zeile „${z.leitungName ?? "Leitung"} ab ${formatDatum(z.validFrom)}: ${zielText(z)}“ zählt danach `
          + `nicht mehr; sie bleibt als Spur stehen. Betroffen: ${anrufeText(anrufe)}.` }))) return;
      uebernehmen(await client.widerrufeAircallZuordnung(customerId, z.id, sicht.version));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setMeldung("Die Aircall-Anbindung wurde inzwischen geändert — der Stand wird neu geladen.");
        neuLaden("Die Aircall-Anbindung wurde inzwischen geändert — der Stand ist neu geladen; bitte erneut prüfen.");
      } else {
        setMeldung(e instanceof ApiError ? e.message : "Die Zuordnung konnte nicht widerrufen werden.");
      }
    } finally {
      setLaeuft(false);
    }
  }

  const gesperrt = p.verdeckt === true || modal !== null || laeuft;
  return (
    <section className="kal" aria-label="Leitungen">
      <h4 className="kal__titel">Leitungen</h4>
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {sicht === null && meldung === null && <p className="kal__leise" role="status">Lädt …</p>}
      {sicht !== null && sicht.leitungen.length === 0 && (
        <p className="kal__leise">Dieser Anbindung ist heute keine Leitung zugeordnet — Anrufe auf ihr werden nicht
          abgerechnet.</p>
      )}
      {sicht !== null && sicht.leitungen.length > 0 && (
        <ul className="kal__liste">
          {sicht.leitungen.map((l) => (
            <li key={l.leitungId} className="kal__zeile">
              <span className="kal__name">{l.name ?? "Leitung ohne Namen"}</span>
              <span className="kal__nummer">{l.digits ?? "—"}</span>
              <span className="kal__leise">gültig ab {formatDatum(l.validFrom)}</span>
              {!l.aktiv && <Chip statusArt="warnung">nicht mehr bei Aircall</Chip>}
              {p.istAdmin && (
                <KiKnopf name={`Zuordnung der Leitung ${leitungText(l)} beenden`} deaktiviert={gesperrt}
                  onClick={() => setModal({ modus: "BEENDEN", leitungId: l.leitungId })}>
                  Zuordnung beenden …
                </KiKnopf>
              )}
            </li>
          ))}
        </ul>
      )}
      {sicht !== null && (
        <div className="kal__aktionen">
          {p.istAdmin && (
            <KiKnopf deaktiviert={gesperrt} onClick={() => setModal({ modus: "ZUORDNEN" })}>Leitung zuordnen …</KiKnopf>
          )}
          {sicht.zuordnungen.length > 0 && (
            <KiKnopf ausgeklappt={zeitreihe} onClick={() => setZeitreihe((z) => !z)}>
              {zeitreihe ? "Zeitreihe verbergen" : `Zeitreihe zeigen (${sicht.zuordnungen.length})`}
            </KiKnopf>
          )}
        </div>
      )}
      {sicht !== null && zeitreihe && (
        <ul className="kal__liste" aria-label="Zeitreihe der Zuordnungen">
          {sicht.zuordnungen.map((z) => (
            <li key={z.id} className={`kal__zeile${z.widerrufenAm === null ? "" : " kal__zeile--widerrufen"}`}>
              <span className="kal__name">{z.leitungName ?? "Leitung"}</span>
              <span>ab {formatDatum(z.validFrom)}: {zielText(z)}</span>
              <span className="kal__leise">
                {z.angelegtVon ?? "unbekannt"}{z.note === null ? "" : ` · ${z.note}`}
                {z.widerrufenAm === null ? "" : ` · widerrufen ${formatDatum(z.widerrufenAm)}`}
              </span>
              {p.istAdmin && z.widerrufenAm === null && eigeneZeile(z, sicht.zuordnungen) && (
                <KiKnopf name={`Zuordnung ${z.leitungName ?? "Leitung"} ab ${formatDatum(z.validFrom)} widerrufen`}
                  deaktiviert={gesperrt} onClick={() => { void widerrufen(z); }}>
                  Widerrufen
                </KiKnopf>
              )}
            </li>
          ))}
        </ul>
      )}
      {sicht !== null && modal !== null && (
        <LeitungModal client={client} customerId={customerId} kundeName={p.kundeName} sicht={sicht}
          modus={modal.modus} {...(modal.leitungId === undefined ? {} : { leitungId: modal.leitungId })}
          onFertig={uebernehmen} onAbbrechen={() => setModal(null)} onKonflikt={neuLaden} />
      )}
    </section>
  );
}
